import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './client'
import type {
  Device,
  Fleet,
  ListResult,
  CreateDeviceRequest,
  UpdateDeviceRequest,
  CreateFleetRequest,
  BulkImportResult,
  DeviceBackends,
  SSHSession,
  UpdateDeviceTypeRequest,
  UpdateSSHCredentialRequest,
  DeviceType,
} from '@/types'

// ---- Devices ---------------------------------------------------------------

export function useDevices(tenantId: string | null, params?: { status?: string; fleet_id?: string; device_type_id?: string; search?: string }) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['devices', tenantId, params],
    queryFn: () => {
      if (!tenantId) return Promise.resolve({ data: [], total: 0 } as ListResult<Device>)
      const qs = new URLSearchParams()
      if (params?.status) qs.set('status', params.status)
      if (params?.fleet_id) qs.set('fleet_id', params.fleet_id)
      if (params?.device_type_id) qs.set('device_type_id', params.device_type_id)
      if (params?.search) qs.set('search', params.search)
      const q = qs.toString()
      return api.get<ListResult<Device>>(
        `/api/v1/tenants/${tenantId}/devices${q ? `?${q}` : ''}`,
      )
    },
    enabled: !!tenantId,
    refetchInterval: 8000,
  })
}

export function useDevice(tenantId: string | null, deviceId: string | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['device', tenantId, deviceId],
    queryFn: () => api.get<Device>(`/api/v1/tenants/${tenantId}/devices/${deviceId}`),
    enabled: !!tenantId && !!deviceId,
  })
}

export function useCreateDevice(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (req: CreateDeviceRequest) =>
      api.post<Device>(`/api/v1/tenants/${tenantId}/devices`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices', tenantId] }),
  })
}

export function useUpdateDevice(tenantId: string, deviceId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (req: UpdateDeviceRequest) =>
      api.put<Device>(`/api/v1/tenants/${tenantId}/devices/${deviceId}`, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices', tenantId] })
      qc.invalidateQueries({ queryKey: ['device', tenantId, deviceId] })
    },
  })
}

/** Set the status of any device in the tenant without knowing the deviceId upfront. */
export function useSetDeviceStatus(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, status }: { deviceId: string; status: string }) =>
      api.put<Device>(`/api/v1/tenants/${tenantId}/devices/${deviceId}`, { status }),
    onSuccess: (_data, { deviceId }) => {
      qc.invalidateQueries({ queryKey: ['devices', tenantId] })
      qc.invalidateQueries({ queryKey: ['device', tenantId, deviceId] })
    },
  })
}

export function useDeleteDevice(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (deviceId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/devices/${deviceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices', tenantId] }),
  })
}

export function useReprovisionDevice(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (deviceId: string) =>
      api.post<Device>(`/api/v1/tenants/${tenantId}/devices/${deviceId}/reprovision`, {}),
    onSuccess: (_data, deviceId) => {
      qc.invalidateQueries({ queryKey: ['devices', tenantId] })
      // Immediately refresh the job list so the new job appears and polling starts.
      qc.invalidateQueries({ queryKey: ['device-jobs', tenantId, deviceId] })
    },
  })
}

export function useImportDevices(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return api.postForm<BulkImportResult>(`/api/v1/tenants/${tenantId}/devices/import`, form)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices', tenantId] }),
  })
}

// ---- Fleets ----------------------------------------------------------------

export function useFleets(tenantId: string | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['fleets', tenantId],
    queryFn: () => api.get<ListResult<Fleet>>(`/api/v1/tenants/${tenantId}/fleets`),
    enabled: !!tenantId,
  })
}

export function useCreateFleet(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (req: CreateFleetRequest) =>
      api.post<Fleet>(`/api/v1/tenants/${tenantId}/fleets`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleets', tenantId] }),
  })
}

export function useDeleteFleet(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (fleetId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/fleets/${fleetId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleets', tenantId] }),
  })
}

export function useUpdateFleet(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ fleetId, req }: { fleetId: string; req: Partial<CreateFleetRequest> }) =>
      api.put<Fleet>(`/api/v1/tenants/${tenantId}/fleets/${fleetId}`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleets', tenantId] }),
  })
}

// ---- SSH Credentials -------------------------------------------------------

export interface SSHCredentialSummary {
  id: string
  tenant_id: string
  name: string
  username: string
  auth_type: 'password' | 'private_key'
  awx_credential_id?: string
  description?: string
  created_at: string
}

export interface CreateSSHCredentialRequest {
  name: string
  username: string
  password?: string
  private_key?: string
  description?: string
}

export function useSSHCredentials(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['ssh-credentials', tenantId],
    queryFn: () => api.get<ListResult<SSHCredentialSummary>>(`/api/v1/tenants/${tenantId}/ssh-credentials`),
    enabled: !!tenantId,
    // Keep polling every 5 s until all credentials have an AWX credential ID.
    refetchInterval: (query) => {
      const d = query.state.data as ListResult<SSHCredentialSummary> | undefined
      if (!d) return false
      return d.data.some(c => !c.awx_credential_id) ? 5000 : false
    },
  })
}

export function useCreateSSHCredential(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateSSHCredentialRequest) =>
      api.post<SSHCredentialSummary>(`/api/v1/tenants/${tenantId}/ssh-credentials`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-credentials', tenantId] }),
  })
}

export function useDeleteSSHCredential(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (credId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/ssh-credentials/${credId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-credentials', tenantId] }),
  })
}

export function useUpdateSSHCredential(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ credId, req }: { credId: string; req: UpdateSSHCredentialRequest }) =>
      api.patch<SSHCredentialSummary>(`/api/v1/tenants/${tenantId}/ssh-credentials/${credId}`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-credentials', tenantId] }),
  })
}

// ---- Device Types ----------------------------------------------------------

export function useDeviceTypes(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['device-types', tenantId],
    queryFn: () =>
      api.get<ListResult<DeviceType>>(`/api/v1/tenants/${tenantId}/device-types`),
    enabled: !!tenantId,
    refetchInterval: (query) => {
      const d = query.state.data as ListResult<DeviceType> | undefined
      if (!d) return false
      return d.data.some(dt => !dt.hawkbit_ds_type_id) ? 5000 : false
    },
  })
}

export function useUpdateDeviceType(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ typeId, req }: { typeId: string; req: UpdateDeviceTypeRequest }) =>
      api.put<DeviceType>(`/api/v1/tenants/${tenantId}/device-types/${typeId}`, req),
    onSuccess: (updated) => {
      qc.setQueryData(['device-types', tenantId], (old: ListResult<DeviceType> | undefined) =>
        old ? { ...old, data: old.data.map(dt => dt.id === updated.id ? updated : dt) } : old
      )
      qc.invalidateQueries({ queryKey: ['device-types', tenantId] })
    },
  })
}

export function useDeleteDeviceType(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (typeId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/device-types/${typeId}`),
    onSuccess: (_data, typeId) => {
      qc.setQueryData(['device-types', tenantId], (old: ListResult<DeviceType> | undefined) =>
        old ? { ...old, data: old.data.filter(dt => dt.id !== typeId) } : old
      )
      qc.invalidateQueries({ queryKey: ['device-types', tenantId] })
    },
  })
}

// ---- SSH Sessions ----------------------------------------------------------

export function useSSHSessions(tenantId: string | null, deviceId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['ssh-sessions', tenantId, deviceId],
    queryFn: async () => {
      const res = await api.get<{ data: SSHSession[] }>(
        `/api/v1/tenants/${tenantId}/devices/${deviceId}/ssh-sessions`,
      )
      return res?.data ?? []
    },
    enabled: !!tenantId && !!deviceId,
  })
}

// ---- Device Backends -------------------------------------------------------

export function useDeviceBackends(tenantId: string | null, deviceId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['device-backends', tenantId, deviceId],
    queryFn: () =>
      api.get<DeviceBackends>(
        `/api/v1/tenants/${tenantId}/devices/${deviceId}/backends`,
      ),
    enabled: !!tenantId && !!deviceId,
  })
}

// ---- Playbooks -------------------------------------------------------------

export interface PlaybookEntry {
  name: string
  path: string
  sha: string
  size: number
}

export interface PlaybookFile {
  name: string
  path: string
  sha: string
  content: string
}

export function usePlaybooks(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['playbooks', tenantId],
    queryFn: () => api.get<PlaybookEntry[]>(`/api/v1/tenants/${tenantId}/playbooks`),
    enabled: !!tenantId,
  })
}

export function usePlaybook(tenantId: string | null, filename: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['playbook', tenantId, filename],
    queryFn: () => api.get<PlaybookFile>(`/api/v1/tenants/${tenantId}/playbooks/${filename}`),
    enabled: !!tenantId && !!filename,
  })
}

export function useSavePlaybook(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ filename, content, message }: { filename: string; content: string; message?: string }) =>
      api.put<{ name: string; path: string; sha: string }>(
        `/api/v1/tenants/${tenantId}/playbooks/${filename}`,
        { content, message },
      ),
    onSuccess: (_data, { filename }) => {
      qc.invalidateQueries({ queryKey: ['playbooks', tenantId] })
      qc.invalidateQueries({ queryKey: ['playbook', tenantId, filename] })
    },
  })
}

// ---- Provisioning Jobs ----------------------------------------------------

export interface JobStep {
  step: string
  status: 'running' | 'success' | 'failed'
  result?: Record<string, unknown>
  error?: string
  at: string
}

export interface ProvisioningJob {
  id: string
  device_id: string
  type: string
  status: 'pending' | 'running' | 'success' | 'failed'
  steps: JobStep[]
  error?: string
  started_at?: string
  completed_at?: string
  created_at: string
}

/** Run a custom Ansible playbook on a specific device via AWX. Returns immediately; poll via useDeviceJobs. */
export function useRunPlaybook(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      deviceId,
      playbook,
      sshCredentialId,
      extraVars,
    }: {
      deviceId: string
      playbook: string
      sshCredentialId?: string
      extraVars?: Record<string, string>
    }) =>
      api.post<ProvisioningJob>(
        `/api/v1/tenants/${tenantId}/devices/${deviceId}/playbook`,
        { playbook, ssh_credential_id: sshCredentialId ?? '', extra_vars: extraVars ?? {} },
      ),
    onSuccess: (_data, { deviceId }) => {
      qc.invalidateQueries({ queryKey: ['device-jobs', tenantId, deviceId] })
    },
  })
}

/** List recent provisioning jobs for a device, newest first. Auto-polls while any job is running. */
export function useDeviceJobs(tenantId: string | null, deviceId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['device-jobs', tenantId, deviceId],
    queryFn: () =>
      api.get<ProvisioningJob[]>(`/api/v1/tenants/${tenantId}/devices/${deviceId}/jobs`),
    enabled: !!tenantId && !!deviceId,
    refetchInterval: (query) => {
      const jobs = query.state.data as ProvisioningJob[] | undefined
      if (!jobs) return false
      return jobs.some(j => j.status === 'pending' || j.status === 'running') ? 2000 : false
    },
  })
}

export interface TenantJob extends ProvisioningJob {
  device_name: string
  fleet_name?: string
}

/** List the 100 most recent provisioning jobs for the whole tenant. Auto-polls while any job is running. */
export function useTenantJobs(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['tenant-jobs', tenantId],
    queryFn: () => api.get<TenantJob[]>(`/api/v1/tenants/${tenantId}/jobs`),
    enabled: !!tenantId,
    refetchInterval: (query) => {
      const jobs = query.state.data as TenantJob[] | undefined
      if (!jobs) return false
      return jobs.some(j => j.status === 'pending' || j.status === 'running') ? 3000 : false
    },
  })
}

// ---- Twin ------------------------------------------------------------------

export interface TwinState {
  state: Record<string, unknown>
  version: number
  updated_at: string
}

export interface Twin {
  device_id: string
  tenant_id: string
  reported: TwinState
  desired?: TwinState
  delta?: Record<string, unknown>
  in_sync: boolean
}

/** Get the full twin (reported + desired + delta). Polls every 5s while out-of-sync. */
export function useTwin(tenantId: string | null, deviceId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['twin', tenantId, deviceId],
    queryFn: () =>
      api.get<Twin>(`/api/v1/tenants/${tenantId}/devices/${deviceId}/twin`),
    enabled: !!tenantId && !!deviceId,
    refetchInterval: (query) => {
      const twin = query.state.data as Twin | undefined
      if (!twin) return 5000
      return twin.in_sync ? 10000 : 3000
    },
  })
}

/** Patch desired state (merge patch). */
export function usePatchDesired(tenantId: string, deviceId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<void>(
        `/api/v1/tenants/${tenantId}/devices/${deviceId}/twin/desired`,
        { patch },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['twin', tenantId, deviceId] }),
  })
}

/** List all twins for a tenant. Polls every 10s. */
export function useListTwins(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['twins', tenantId],
    queryFn: () =>
      api.get<{ results: Twin[] }>(`/api/v1/tenants/${tenantId}/twins`),
    enabled: !!tenantId,
    refetchInterval: 10000,
  })
}
